import { MdCached, MdWarning } from 'react-icons/md';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatPacingNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Placement } from '@Components/designConstants';
import { PacingBarLayer } from '@Pages/Pacing/components/PacingBar';
import { PacingBarColors } from '@src/utils/colors';

interface DeliverySpends {
  secured: number;
  unsecured: number;
  // Only the By Campaign tab splits upfront out of secured.
  upfront?: number;
  estimated: number;
}

// bpm-frontend drew these as overlapping fills from zero, upfront on top of secured on top of
// unsecured (which spans secured + unsecured) on top of pacing. As stacked segments, each one
// covers only what the fills above it leave visible.
export const deliveryLayers = ({
  secured,
  unsecured,
  upfront,
  estimated
}: DeliverySpends): PacingBarLayer[] => {
  let covered = 0;
  const visibleUpTo = (end: number) => {
    const width = Math.max(end - covered, 0);
    covered = Math.max(covered, end);
    return width;
  };

  return [
    ...(upfront === undefined
      ? []
      : [
          {
            key: 'upfront',
            value: visibleUpTo(upfront),
            color: PacingBarColors.deliveryUpfront,
            label: formatPacingNumber(upfront, '$')
          }
        ]),
    {
      key: 'secured',
      value: visibleUpTo(secured),
      color: PacingBarColors.deliverySecured,
      label: formatPacingNumber(secured, '$')
    },
    {
      key: 'unsecured',
      value: visibleUpTo(secured + unsecured),
      color: PacingBarColors.deliveryUnsecured,
      label: formatPacingNumber(unsecured, '$')
    },
    { key: 'pacing', value: visibleUpTo(estimated), color: PacingBarColors.pacing }
  ];
};

export const reprocessingLayers = (max: number): PacingBarLayer[] => [
  {
    key: 'pacing',
    value: max,
    color: PacingBarColors.pacing,
    label: (
      <div>
        Reprocessing&nbsp;
        <MdCached />
      </div>
    )
  }
];

// A share of Booked, or "—" when nothing is booked.
export const vsBookedPercent = (value: number, booked: number): string =>
  booked ? `${Math.round((value / booked) * 100)}%` : '—';

export const pacingLabel = (estimated: number, booked: number): string =>
  `${vsBookedPercent(estimated, booked)} / ${formatPacingNumber(estimated, '$')}`;

// KPI cards for the selection the right pane drills into, in the legend's terms.
export const pacingKpis = (
  booked: number,
  logs: number,
  estimated: number
): { label: string; value: string }[] => [
  { label: 'Booked', value: formatPacingNumber(booked, '$') },
  { label: 'On the Logs', value: formatPacingNumber(logs, '$') },
  { label: 'Pacing', value: formatPacingNumber(estimated, '$') },
  {
    label: 'Pacing vs Booked',
    value: vsBookedPercent(estimated, booked)
  }
];

// The bar's alert icon, with a tooltip saying why it's shown.
const warningIcon = (reason: string) => (
  <OverlayTrigger placement={Placement.TOP} overlay={<Tooltip>{reason}</Tooltip>}>
    <span className='warning-icon'>
      <MdWarning />
    </span>
  </OverlayTrigger>
);

export const NO_PRELOGS_WARNING = warningIcon('No pre-logs received yet');
export const NO_COMMISSION_OR_PRELOGS_WARNING = warningIcon(
  'No commission rate and no pre-logs received yet'
);
